import { Button, Checkbox, Space, TimePicker } from 'antd';
import dayjs from 'dayjs';
import { DateTime } from 'luxon';
import { useState } from 'react';

import { ShiftListItemDto, ShiftTimeWorkedDtoTypeEnum } from '@clh/api-client';
import { useApiClient } from '@clh/ui';
import {
    getShiftDuration,
    roundToAtMostTwoDecimals,
    toDateISOTimeStr,
} from '@clh/util';

import { ActionButton } from '../../../action-button';
import { useSelection } from '../../../hooks/use-selection';

import { calculateActualClockTimes } from './calculate-actual-clock-times';
import { TimeWorkedSummary } from './time-worked-summary';
import { useTimeCalculations } from './use-time-calculations';

const getInitialClockTimes = (selection: ShiftListItemDto) => {
    if (selection.timeWorked?.length) {
        const standardTimeWorked = selection.timeWorked.find(
            (tw) =>
                tw.type === ShiftTimeWorkedDtoTypeEnum.Standard ||
                tw.type === ShiftTimeWorkedDtoTypeEnum.CalledIn
        );
        if (standardTimeWorked) {
            return {
                clockInTime: dayjs(standardTimeWorked.startTime),
                clockOutTime: dayjs(standardTimeWorked.endTime),
                tookMealBreak: standardTimeWorked.mealBreakMinutes > 0,
            };
        }
    }
    return {
        clockInTime: dayjs(selection.startTime),
        clockOutTime: dayjs(selection.endTime),
        tookMealBreak: selection.submittedTimeAt
            ? !selection.skippedMealBreak
            : false,
    };
};

export const LogTime = ({ close }: { close: () => void }) => {
    const { selection, setSelection } = useSelection<ShiftListItemDto>();
    const api = useApiClient();

    const initialTimes = getInitialClockTimes(selection);

    const [shiftTimeWorked, setShiftTimeWorked] = useState({
        wasCalledIn: false,
        didNotWork:
            selection.didNotWork !== null ? selection.didNotWork : false,
        tookMealBreak: initialTimes.tookMealBreak,
        clockInTime: initialTimes.clockInTime,
        clockOutTime: initialTimes.clockOutTime,
    });

    const updateTimeWorked = (changes: Partial<typeof shiftTimeWorked>) =>
        setShiftTimeWorked({ ...shiftTimeWorked, ...changes });

    const { worked, onCall } = useTimeCalculations(shiftTimeWorked, selection);

    if (!selection.nurseProfile) {
        return null;
    }

    const shiftDuration = getShiftDuration(
        selection.shiftTime,
        toDateISOTimeStr(selection.startTime),
        toDateISOTimeStr(selection.endTime),
        true
    );

    return (
        <Space direction="vertical" style={{ width: '400px' }}>
            <span>
                {DateTime.fromJSDate(selection.startTime).toFormat(
                    'MMMM d, yyyy, HHmm'
                )}
                -{DateTime.fromJSDate(selection.endTime).toFormat('HHmm')} (
                {roundToAtMostTwoDecimals(shiftDuration)}hrs)
            </span>
            {selection.onCallShift && (
                <Checkbox
                    checked={shiftTimeWorked.wasCalledIn}
                    onChange={() =>
                        updateTimeWorked({
                            wasCalledIn: !shiftTimeWorked.wasCalledIn,
                        })
                    }
                >
                    Called back
                </Checkbox>
            )}
            <Checkbox
                checked={shiftTimeWorked.didNotWork}
                onChange={() =>
                    updateTimeWorked({
                        didNotWork: !shiftTimeWorked.didNotWork,
                    })
                }
            >
                Did not work
            </Checkbox>
            {!selection.onCallShift || shiftTimeWorked.wasCalledIn ? (
                <>
                    <Checkbox
                        checked={shiftTimeWorked.tookMealBreak}
                        disabled={shiftTimeWorked.didNotWork}
                        onChange={() =>
                            updateTimeWorked({
                                tookMealBreak: !shiftTimeWorked.tookMealBreak,
                            })
                        }
                    >
                        Took meal break
                    </Checkbox>
                    Clock in / out
                    <TimePicker.RangePicker
                        format={'HH:mm'}
                        disabled={shiftTimeWorked.didNotWork}
                        needConfirm={false}
                        defaultValue={[
                            shiftTimeWorked.clockInTime,
                            shiftTimeWorked.clockOutTime,
                        ]}
                        onChange={(times) => {
                            if (!times) return;
                            updateTimeWorked({
                                clockInTime: times[0] ?? undefined,
                                clockOutTime: times[1] ?? undefined,
                            });
                        }}
                        placeholder={['Clock-in', 'Clock-out']}
                    />
                </>
            ) : null}
            <TimeWorkedSummary
                worked={worked}
                onCall={onCall}
                tookMealBreak={shiftTimeWorked.tookMealBreak}
                didNotWork={shiftTimeWorked.didNotWork}
            />
            <Space>
                <ActionButton
                    successMessage="Time submitted"
                    action={async () => {
                        let clockInTime: string | undefined;
                        let clockOutTime: string | undefined;

                        if (
                            !shiftTimeWorked.didNotWork &&
                            (selection.onCallShift
                                ? shiftTimeWorked.wasCalledIn
                                : true)
                        ) {
                            const scheduledStart = DateTime.fromJSDate(
                                selection.startTime
                            );
                            const { actualClockIn, actualClockOut } =
                                calculateActualClockTimes(
                                    scheduledStart,
                                    shiftTimeWorked.clockInTime,
                                    shiftTimeWorked.clockOutTime
                                );

                            clockInTime = actualClockIn.toISO() ?? undefined;
                            clockOutTime = actualClockOut.toISO() ?? undefined;
                        }

                        const timeWorked =
                            await api?.shiftControllerSubmitTimeWorked({
                                shiftId: selection.id,
                                submitTimeWorkedDto: {
                                    didNotWork: shiftTimeWorked.didNotWork,
                                    clockEntries:
                                        clockInTime && clockOutTime
                                            ? [
                                                  {
                                                      clockInTime,
                                                      clockOutTime,
                                                      tookMealBreak:
                                                          shiftTimeWorked.tookMealBreak,
                                                  },
                                              ]
                                            : [],
                                },
                            });

                        const { updatedOnCall, updatedWorkedMinutes } = (
                            timeWorked ?? []
                        ).reduce(
                            (acc, segment) => {
                                if (
                                    segment.type ===
                                    ShiftTimeWorkedDtoTypeEnum.OnCall
                                ) {
                                    acc.updatedOnCall += segment.minutesWorked;
                                } else {
                                    acc.updatedWorkedMinutes +=
                                        segment.minutesWorked -
                                        segment.mealBreakMinutes;
                                }
                                return acc;
                            },
                            { updatedOnCall: 0, updatedWorkedMinutes: 0 }
                        );

                        setSelection({
                            ...selection,
                            timeWorked,
                            minutesWorked: updatedWorkedMinutes,
                            onCallShift: selection.onCallShift
                                ? {
                                      ...selection.onCallShift,
                                      minutesWorked: updatedOnCall,
                                  }
                                : undefined,
                            didNotWork: shiftTimeWorked.didNotWork,
                            skippedMealBreak: !shiftTimeWorked.tookMealBreak,
                        });

                        close();
                    }}
                >
                    Submit
                </ActionButton>
                <Button onClick={close}>Cancel</Button>
            </Space>
        </Space>
    );
};
